import {useCallback, useMemo} from "react";
import {
    Grid, Button, FormControl, InputLabel, Select, MenuItem, FormHelperText, Autocomplete, TextField,
} from "@mui/material";
import {DateTime} from "luxon";
import {Controller} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useEnrollmentForm} from "../hooks/useEnrollmentForm";
import {useGetStartDates} from "../../../hooks/useGetStartDates";
import {useGetGrades} from "../../../hooks/useGetGrades";
import Preloader from "../../../components/Preloader";
import DatePickerInput from "../../../components/DatePickerInput";
import {formatToISO} from "../../../utils/dateTimeHelper";
import {useGetPerformanceAreas} from "../../../hooks/useGetPerformanceAreas";

export const EnrollmentForm = (props) => {
    const {data, onFinish} = props;
    const {t} = useTranslation();

    const {isLoading: isLoadingStartDates, startDatesOptions} = useGetStartDates();
    const {isLoading: isLoadingGrades, gradesOptions} = useGetGrades();
    const {isLoading: isLoadingPerformanceAreas, performanceAreasOptions} = useGetPerformanceAreas();

    const minSelectableStartDate = useMemo(() => DateTime.now().plus({days: 3}).startOf("day"), []);
    const shouldDisableStartDate = useCallback((date) => date.weekday > 5, []);

    const {
        handleSubmit, control, errors, isDirty,
    } = useEnrollmentForm(data);

    const onSubmit = (dataForm) => {
        const {desiredStartDate, ...formData} = dataForm;
        const dataFormatted = {
            ...formData,
            desiredStartDate: formatToISO(desiredStartDate),
        };
        onFinish?.(dataFormatted, isDirty);
    };

    return (
        <Preloader isLoading={isLoadingStartDates || isLoadingGrades || isLoadingPerformanceAreas} overflow>
            <form className="Students-form" onSubmit={handleSubmit(onSubmit)}>
                <Grid container rowSpacing={3} columnSpacing={2}>
                    <Grid item size={{xs: 12, md: 6}}>
                        <DatePickerInput
                            required
                            label={t("students.enrollment.desiredStartDate")}
                            control={control}
                            name="desiredStartDate"
                            placeholder="MM/DD/YYYY"
                            error={errors.desiredStartDate?.message}
                            minDate={minSelectableStartDate}
                            shouldDisableDate={shouldDisableStartDate}
                        />
                    </Grid>
                    <Grid item size={{xs: 12, md: 6}}>
                        <FormControl fullWidth error={!!errors.notionalYearGroupId}>
                            <InputLabel required>{t("students.enrollment.gradeLevel")}</InputLabel>
                            <Controller
                                name="notionalYearGroupId"
                                control={control}
                                render={({field}) => (
                                    <Select {...field} onChange={(event) => field.onChange(event.target.value)}>
                                        <MenuItem value="">
                                            <em className="select-placeholder">{t("students.enrollment.placeholders.gradeLevel")}</em>
                                        </MenuItem>
                                        {gradesOptions.map((item) => (
                                            <MenuItem key={item.value} value={item.value}>
                                                {item.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors.notionalYearGroupId?.message ? (
                                <FormHelperText>{errors.notionalYearGroupId.message}</FormHelperText>
                            ) : null}
                        </FormControl>
                    </Grid>
                    <Grid item size={12}>
                        <FormControl fullWidth error={!!errors.performanceAreaId}>
                            <InputLabel required>{t("students.enrollment.mainPerformanceArea")}</InputLabel>
                            <Controller
                                name="performanceAreaId"
                                control={control}
                                render={({field}) => (
                                    <Autocomplete
                                        {...field}
                                        value={performanceAreasOptions.find((opt) => opt.value === field.value) || null}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue?.value || null);
                                        }}
                                        disablePortal
                                        options={performanceAreasOptions}
                                        renderInput={(params) => <TextField {...params} placeholder={t("students.enrollment.placeholders.mainPerformanceArea")} />}
                                    />
                                )}
                            />
                            {errors.performanceAreaId?.message ? (
                                <FormHelperText>{errors.performanceAreaId.message}</FormHelperText>
                            ) : null}
                        </FormControl>
                    </Grid>
                    <Grid item size={12} className="w-full flex">
                        <Button
                            variant="accent"
                            type="submit"
                            className="ml-auto"
                        >
                            {t("students.addInformation")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Preloader>
    );
};
